import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import {persistor } from '../../Redux/Store'

const MobileNavigation = ({ user, isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

 const navigationItems = [
    { 
      label: 'Dashboard', 
      path: user?.role === 'admin' ? '/admin-dashboard' : '/vendor-dashboard',
      icon: 'LayoutDashboard',
      roles: ['admin', 'vendor', 'employee'],
      description: 'Overview and metrics'
    },
    { 
      label: 'Products', 
      path: '/product-management',
      icon: 'Package',
      roles: ['vendor'],
      description: 'Manage product catalog'
    },
    { 
      label: 'Employees', 
      path: '/employee-management',
      icon: 'Users',
      roles: ['vendor'],
      description: 'Team management'
    },
    {
      label: 'Expenses',
      path: '/expenses',
      icon: 'CreditCard',
      roles: ['vendor'],
      description: 'Track expenses'
    },
    {
      label: 'Customers',
      path: '/customers',
      icon: 'Users',
      roles: ['vendor'],
      description: 'Manage customers'
    },
    { 
      label: 'Settings', 
      path: '/user-profile-settings',
      icon: 'Settings',
      roles: ['admin', 'vendor', 'employee'],
      description: 'Account preferences'
    },
  ];

  const filteredNavItems = navigationItems.filter(item => 
    item.roles.includes(user?.role)
  );

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    persistor.purge();

    navigate('/whats-app-authentication');
    onClose();
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'vendor': return 'Vendor Manager';
      case 'employee': return 'Employee';
      default: return 'User';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1100 lg:hidden">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity duration-200" 
        onClick={onClose}
      />
      
      {/* Slide-out Panel */}
      <div className="fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-card border-r border-border animate-slide-in">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                  <Icon name="Building2" size={20} color="white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-foreground">VendorHub</h1>
                  <p className="text-xs text-muted-foreground -mt-1">Admin Panel</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="micro-animation"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            {/* User Info */}
            <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-md">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="User" size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {user?.name || 'User Name'}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {getRoleDisplayName(user?.role)}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            <div className="mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Navigation
              </p>
              {filteredNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                
                return (
                  <Button
                    key={item.path}
                    variant={isActive ? "default" : "ghost"}
                    fullWidth
                    onClick={() => handleNavigation(item.path)}
                    className="justify-start mb-1 micro-animation"
                  >
                    <Icon name={item.icon} size={20} />
                    <div className="ml-3 text-left">
                      <div className="text-sm font-medium">{item.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.description}
                      </div>
                    </div>
                  </Button>
                );
              })}
            </div>
          </nav>

          {/* Footer Actions */}
          <div className="p-4 border-t border-border space-y-2">
            <Button
              variant="ghost"
              fullWidth
              onClick={() => handleNavigation('/user-profile-settings')}
              className="justify-start micro-animation"
            >
              <Icon name="Settings" size={20} />
              <span className="ml-3">Profile Settings</span>
            </Button>
            
            <Button
              variant="ghost"
              fullWidth
              onClick={handleLogout}
              className="justify-start text-destructive hover:text-destructive micro-animation"
            >
              <Icon name="LogOut" size={20} />
              <span className="ml-3">Sign Out</span>
            </Button>
            
            {/* App Info */}
            <div className="pt-4 mt-4 border-t border-border">
              <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
                <Icon name="Building2" size={14} />
                <span>VendorHub v2.1.0</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileNavigation;